import { useCallback, useEffect, useState } from "react";

import { DEFAULT_SHORTCUTS, TShortcutKey } from "./links";

const STORAGE_KEY = "nav-shortcuts";
// Same tab gets no storage event, and the strip and the panel are separate
// subtrees, so they sync through this instead.
const CHANGE_EVENT = "nav-shortcuts-change";

const read = (): TShortcutKey[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_SHORTCUTS;
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : DEFAULT_SHORTCUTS;
  } catch {
    return DEFAULT_SHORTCUTS;
  }
};

/**
 * Which personal links sit in the strip.
 *
 * ponytail: localStorage, so pins are per device and per browser. Move to a
 * user preference on the account if people start expecting them to follow them.
 */
export const useShortcuts = () => {
  const [pinned, setPinned] = useState<TShortcutKey[]>(read);

  useEffect(() => {
    const sync = () => setPinned(read());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggle = useCallback((key: TShortcutKey) => {
    // Re-read rather than close over `pinned`: the strip and the panel both hold
    // this hook, and the other one's copy must not overwrite this change.
    const current = read();
    const next = current.includes(key)
      ? current.filter((pin) => pin !== key)
      : [...current, key];

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Private browsing and the like: the pin still applies for this session.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return {
    pinned,
    toggle,
    isPinned: (key: TShortcutKey) => pinned.includes(key),
  };
};
